import { ScheduleOfValue } from 'components/features/schedule-of-values/schedule-of-values-table/types';
import { ValidationError } from 'components/ui/excel-table/types';
import { TFunction } from 'i18next';
import { UpsertScheduleOfValuesPayloadDto } from 'libs/pinnacle-sdk';
import { isInteger, isNil } from 'lodash';
import { cleanStringField } from './utils';

export const validateScheduleOfValue = (
  row: ScheduleOfValue,
  rowIndex: number,
  _: ScheduleOfValue[],
  t: TFunction
): ValidationError<ScheduleOfValue>[] => {
  const errors: ValidationError<ScheduleOfValue>[] = [];

  // Country validation - Required
  if (!row.country) {
    errors.push({
      field: 'country',
      message: t('validation.required', {
        field: t('general.country'),
      }),
      rowIndex,
    });
  }

  // Cresta Zone validation - Required
  if (!row.crestaZone) {
    errors.push({
      field: 'crestaZone',
      message: t('validation.required', {
        field: t('scheduleOfValues.crestaZone'),
      }),
      rowIndex,
    });
  }

  // Occupancy validation - Required
  if (!row.occupancy) {
    errors.push({
      field: 'occupancy',
      message: t('validation.required', {
        field: t('scheduleOfValues.occupancy'),
      }),
      rowIndex,
    });
  }

  // PD Deductibles validation - Optional, Numeric
  if (row.pdDeductibles !== undefined && isNaN(row.pdDeductibles)) {
    errors.push({
      field: 'pdDeductibles',
      message: t('validation.numeric', {
        field: t('scheduleOfValues.pdDeductibles'),
      }),
      rowIndex,
    });
  }

  // BI Value validation - Optional, Numeric
  if (row.biValue !== undefined && isNaN(row.biValue)) {
    errors.push({
      field: 'biValue',
      message: t('validation.numeric', {
        field: t('scheduleOfValues.biValue'),
      }),
      rowIndex,
    });
  }

  // Building Value validation - Optional, Numeric
  if (row.buildingValue !== undefined && isNaN(row.buildingValue)) {
    errors.push({
      field: 'buildingValue',
      message: t('validation.numeric', {
        field: t('scheduleOfValues.buildingValue'),
      }),
      rowIndex,
    });
  }

  // CPE validation - Optional, Numeric
  if (row.cpe !== undefined && isNaN(row.cpe)) {
    errors.push({
      field: 'cpe',
      message: t('validation.numeric', {
        field: t('scheduleOfValues.cpe'),
      }),
      rowIndex,
    });
  }

  // Stock validation - Optional, Numeric
  if (row.stock !== undefined && isNaN(row.stock)) {
    errors.push({
      field: 'stock',
      message: t('validation.numeric', {
        field: t('scheduleOfValues.stock'),
      }),
      rowIndex,
    });
  }

  // Number of Stories validation - Optional, Numeric, Positive
  if (
    !isNil(row.numberOfStories) &&
    (!isInteger(Number(row.numberOfStories)) || row.numberOfStories <= 0)
  ) {
    errors.push({
      field: 'numberOfStories',
      message: t('validation.wholePositiveField', {
        field: t('scheduleOfValues.numberOfStories'),
      }),
      rowIndex,
    });
  }

  // Latitude validation - Optional, between -90 and 90
  if (
    row.latitude !== undefined &&
    (isNaN(row.latitude) || row.latitude < -90 || row.latitude > 90)
  ) {
    errors.push({
      field: 'latitude',
      message: t('scheduleOfValues.validLatitude'),
      rowIndex,
    });
  }

  // Longitude validation - Optional, between -180 and 180
  if (
    row.longitude !== undefined &&
    (isNaN(row.longitude) || row.longitude < -180 || row.longitude > 180)
  ) {
    errors.push({
      field: 'longitude',
      message: t('scheduleOfValues.validLongitude'),
      rowIndex,
    });
  }

  // Year Built validation - Optional, must be between 0 and current year + 5
  const currentYear = new Date().getFullYear();
  const maxYear = currentYear + 5;
  if (
    !isNil(row.yearBuilt) &&
    String(row.yearBuilt).trim() !== '' &&
    (isNaN(row.yearBuilt) || row.yearBuilt <= 0 || row.yearBuilt > maxYear)
  ) {
    errors.push({
      field: 'yearBuilt',
      message: t('scheduleOfValues.validYearBuilt', {
        maxYear,
      }),
      rowIndex,
    });
  }

  return errors;
};

export const calculatePdValue = (row: ScheduleOfValue): number => {
  return (
    Number(row.buildingValue ?? 0) +
    Number(row.cpe ?? 0) +
    Number(row.stock ?? 0)
  );
};

export const calculateTotalInsurableValue = (row: ScheduleOfValue): number => {
  return calculatePdValue(row) + Number(row.biValue ?? 0);
};

export const newRowData: ScheduleOfValue = {
  property: undefined,
  streetAddress: undefined,
  suburb: undefined,
  state: undefined,
  postalZipCode: undefined,
  constructionClass: undefined,
  roofType: undefined,
  numberOfStories: undefined,
  yearBuilt: undefined,
  fluvialFlood: undefined,
  originalStormSurge: undefined,
  originalPfRiskLevel: undefined,
  windstorm: undefined,
  hailstorm: undefined,
  wildfire: undefined,
  latitude: undefined,
  longitude: undefined,
  pdDeductibles: undefined,
  biValue: undefined,
  buildingValue: undefined,
  cpe: undefined,
  stock: undefined,
  crestaZone: undefined,
  country: undefined,
  occupancy: undefined,
  pdValue: 0,
  totalInsurableValue: 0,
};

export const convertScheduleOfValuesToPayload = (
  scheduleOfValues: ScheduleOfValue[],
  submissionId: string
): UpsertScheduleOfValuesPayloadDto[] => {
  return scheduleOfValues.map((sov) => ({
    ...sov,
    pdValue: String(calculatePdValue(sov)),
    pdDeductibles: sov.pdDeductibles ? String(sov.pdDeductibles) : undefined,
    biValue: sov.biValue ? String(sov.biValue) : undefined,
    buildingValue: sov.buildingValue ? String(sov.buildingValue) : undefined,
    cpe: sov.cpe ? String(sov.cpe) : undefined,
    stock: sov.stock ? String(sov.stock) : undefined,
    latitude: sov.latitude ? String(sov.latitude) : undefined,
    longitude: sov.longitude ? String(sov.longitude) : undefined,
    numberOfStories: cleanStringField(sov.numberOfStories),
    yearBuilt: cleanStringField(sov.yearBuilt),
    locationNumber: String(sov.locationNumber),
    totalInsurableValue: String(calculateTotalInsurableValue(sov)),
    submissionId,
  }));
};
